import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgModel } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadModule, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, OperatorFunction, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { TextListViewComponent } from './list-view.component';
import { InputContainerComponent } from '../input-container/input-container.component';

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule, NgbTypeaheadModule, InputContainerComponent],
    selector: 'wd-typeahead',
    templateUrl: 'typeahead-input.component.html',
    styleUrls: ['../styles/view.component.css', '../styles/input.component.css', '../styles/typeahead-input.component.css'],
})
export class TypeaheadInputComponent extends TextListViewComponent implements OnInit {
	@Input() placement = 'bottom';
	// default behavior: expand on click or key press
	@Input() expandOnFocus = false;
	@Input() expandOnClick = true;
	@Input() maxItems = 0;
	@Input() filter?: (v: string, idx?: number) => boolean;
	@Input() hbfFilter?: string;
	/** Regex-Validierung nur für Freitext-Werte (nicht in der Liste). Benötigt acceptOtherValues=true. */
	@Input() otherValuesRegex?: string;
	@Input() otherValuesRegexMessage = 'Ungültiger Freitext-Wert';
	/** Zusätzliche Vorschläge die mit der statischen Liste zusammengeführt werden */
	@Input() extraSuggestions: string[] = [];
	@Output() hbfSelected = new EventEmitter<string>();
	@ViewChild('instance', { static: true }) instance?: NgbTypeahead;
	@ViewChild("thisInput") input!: ElementRef;
	@ViewChild('inputValue') inputValue!: NgModel;

	focus$ = new Subject<string>();
	click$ = new Subject<string>();

	override ngOnInit(): void {
		super.ngOnInit();

		const opt = this.os.option(this.name);
		if (opt) {
			if (opt.expandOnFocus !== undefined) this.expandOnFocus = opt.expandOnFocus;
			if (opt.expandOnClick !== undefined) this.expandOnClick = opt.expandOnClick;
			if (opt.maxItems !== undefined) this.maxItems = opt.maxItems;
		}
	}

	formatter = (x: string) => x;

	search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => {

		const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
		const isAllowed = (v: string, index: number) => {
			if (!this.filter) return true;
			if (!this.tableColumn) return this.filter(v.trim());
			return this.filter(v.trim(), this.table[index]);
		};
		const filterFunc = (term: string) => {
			return (v: string, index: number) => {
				const isContained = v?.trim().toLowerCase().indexOf(term?.toLowerCase()) > -1;
				return isAllowed(v, index) && isContained;
			};
		};
		const mapper = map((term: string, index: number) => {
			const filtered = term?.length === 0
				? this.data?.filter(isAllowed)
				: this.data?.filter(filterFunc(term?.trim()));

			// Extra-Vorschläge einbeziehen (nach Suchbegriff filtern)
			let extraFiltered: string[] = [];
			if (this.extraSuggestions?.length > 0) {
				const t = term?.trim().toLowerCase() || '';
				extraFiltered = t.length === 0
					? this.extraSuggestions
					: this.extraSuggestions.filter(v => v.toLowerCase().includes(t));
			}

			// Hauptdaten alphabetisch sortieren
			const mainValues = [...new Set(filtered.map((v: string) => v?.trim()))].sort((a: string, b: string) => {
				const A = a?.toLowerCase(), B = b?.toLowerCase(), TERM = term?.toLowerCase();
				if (A.startsWith(TERM)) {
					if (B.startsWith(TERM)) return A == B ? 0 : (A < B ? -1 : 1);
					else return -1;
				}
				else if (B.startsWith(TERM)) return 1;
				return A == B ? 0 : (A < B ? -1 : 1);
			});

			// Extra-Vorschläge behalten ihre Reihenfolge (priorisiert) und kommen vor den Hauptdaten
			const extraSet = new Set(extraFiltered);
			const uniqueMain = mainValues.filter(v => !extraSet.has(v));
			const sorted = [...extraFiltered, ...uniqueMain];

			return this.maxItems > 0 ? sorted.slice(0, this.maxItems) : sorted;
		});

		const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance?.isPopupOpen()));
		const inputFocus$ = this.focus$;
		if (this.expandOnFocus || this.expandOnClick) {
			const triggers = [debouncedText$];
			if (this.expandOnFocus) triggers.push(inputFocus$);
			if (this.expandOnClick) triggers.push(clicksWithClosedPopup$);
			return merge(...triggers).pipe(mapper);
		}
		else {
			return debouncedText$.pipe(mapper);
		}
	}

	override onChange(changes?: SimpleChanges | undefined) {
		if (changes && changes['model'] && changes['model'].isFirstChange() && this.data.length == 0) {
			super.onChange(changes);
			return;
		}
		// Dirty-Flag immer setzen bei User-Änderungen, auch bei Validierungsfehlern
		if (!changes && this.initialized) {
			this.vs.dirty.next(true);
		}
		// Validierung durchführen (setzt ggf. Fehlermeldung)
		const valid = this.baseValidate();
		// Model immer aktualisieren, auch bei Validierungsfehlern
		if (valid || this.successOnDanger()) {
			super.onChange(changes);
		} else {
			// Bei Validierungsfehler: Model trotzdem emittieren, damit Two-Way-Binding funktioniert
			this.modelChange.emit(this.model);
		}
		// Bei manueller Eingabe einer bekannten FBF: zugehörige HBF automatisch setzen
		if (!changes) {
			const value = this.input?.nativeElement?.value || this.model;
			this.emitHbfForFbf(value);
		}
	}

	protected override baseValidate(): boolean {
		if (!super.baseValidate()) return this.successOnDanger();
		const valueToCheck = this.input && this.input.nativeElement.value ? this.input.nativeElement.value : this.model;
		return this.doBaseValidate(valueToCheck);
	}

	protected override doBaseValidate(valueToCheck: string|undefined) {
		if (!this.nonEmpty && !valueToCheck) return true;
		if (this.nonEmpty && !valueToCheck && !this.touched) return true; // Nicht validieren bevor Feld angefasst wurde
		if (valueToCheck === undefined || valueToCheck === null) return true;

		if (this.data.length > 0 && this.data.indexOf(valueToCheck.toString()) == -1) {
			if (this.acceptOtherValues) {
				// Regex-Validierung für Freitext-Werte (die nicht in der Liste stehen)
				if (this.otherValuesRegex && !valueToCheck.match(this.otherValuesRegex)) {
					this.danger(this.otherValuesRegexMessage);
					return this.successOnDanger();
				}
				return true;
			}
			this.danger("Wert nicht in der Liste");
			return this.successOnDanger();
		}

		return true;
	}

	override onFocus($event: FocusEvent) {
		if (!this.disabled() && $event.target) {
			($event.target as HTMLInputElement).select(); // Text bei Fokus erst einmal komplett selektieren
			this.focus$.next(($event.target as HTMLInputElement).value);
		}
		super.onFocus($event);
	}

	override onClick($event: MouseEvent) {
		if (!this.disabled() && $event.target) this.click$.next(($event.target as HTMLInputElement).value);
		super.onClick($event);
	}

	private _lastHelpText = '';

	override helpText(): string {
		// Nur aktualisieren wenn inputValue touched ist, um ExpressionChangedAfterItHasBeenCheckedError zu vermeiden
		if (this.inputValue === undefined || this.inputValue.touched) {
			this._lastHelpText = super.helpText();
		}
		return this._lastHelpText;
	}

	override onFocusOut() {
		const val = this.input?.nativeElement?.value?.trim();
		if (!val || !this.data?.length) { super.onFocusOut(); return; }

		// Exakter Treffer → nichts zu tun
		if (this.data.indexOf(val) > -1) { super.onFocusOut(); return; }

		// Besten Treffer suchen (gleiche Logik wie Dropdown-Suche)
		const term = val.toLowerCase();
		const matches = this.data
			.filter((v: string, i: number) => {
				if (this.filter) {
					const allowed = this.tableColumn ? this.filter(v.trim(), this.table[i]) : this.filter(v.trim());
					if (!allowed) return false;
				}
				return v?.trim().toLowerCase().includes(term);
			})
			.map((v: string) => v.trim());

		const unique = [...new Set(matches)].sort((a, b) => {
			const A = a.toLowerCase(), B = b.toLowerCase();
			if (A.startsWith(term)) {
				if (B.startsWith(term)) return A < B ? -1 : (A > B ? 1 : 0);
				return -1;
			}
			if (B.startsWith(term)) return 1;
			return A < B ? -1 : (A > B ? 1 : 0);
		});

		if (unique.length > 0 && unique[0] !== val) {
			this.model = unique[0];
			this.input.nativeElement.value = this.model;
			this.onChange();
		}
		super.onFocusOut();
	}

	onSelect($event: NgbTypeaheadSelectItemEvent) {
		// Nur ändern wenn sich der Wert tatsächlich unterscheidet
		if (this.model === $event.item) return;
		this.model = $event.item;
		this.onChange(); // ruft auch emitHbfForFbf auf
	}

	// Sucht die HBF zur ausgewählten FBF und emittiert sie
	private emitHbfForFbf(fbf: string) {
		if (!this.tableColumn || this.tableColumn !== 'FBF' || !this.table) return;
		const entry = this.table.find((e: any) => e.FBF?.trim() === fbf?.trim());
		if (entry?.HBF) {
			this.hbfSelected.emit(entry.HBF);
		}
	}

	onHelpValueSelected(value: {fbf: string, hbf: string}) {
		this.model = value.fbf;
		this.onChange();
		// HBF-Wert nach oben weitergeben
		if (value.hbf) {
			this.hbfSelected.emit(value.hbf);
		}
	}
}
